import React from 'react'

import {
  Box,
  Button,
  Icon,
  SearchField,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { orderBy } from 'lodash'

import ScrollableList from 'modules/core/components/ScrollableList'
import useSearch from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

export interface Field {
  title: string
  selected: boolean
  onClick: () => void
}

interface Props {
  header: string
  reset?: string
  icon?: React.ComponentProps<typeof Icon.Small>['icon']
  onClickHeader: () => void
  getFields: () => Field[]
}

const ASC = 'asc'
type SortDirection = typeof ASC

const useFields = (fields: Field[], search: string, direction: SortDirection) => {
  const templatesToDisplay = fields.filter((field: Field) => {
    return matchText(search, field.title)
  })

  return orderBy(templatesToDisplay, (field) => field.title, [direction])
}

const BlankList = () => {
  return (
    <Box alignItems="center" padding="s40" width="full">
      <Text.Normal>{i18n.t('filters.default_filters.filter_not_found')}</Text.Normal>
    </Box>
  )
}

const FieldOptions = ({ field }: { field: Field }) => {
  const backgroundColor = field.selected ? 'viridian100' : 'grey000White'

  return (
    <Box
      style={{ maxHeight: '256px' }}
      background={backgroundColor}
      borderRadius={{
        all: 'abs008',
      }}
      alignItems="center"
      justifyContent="flexStart"
      cursor="pointer"
      flexDirection="row"
      padding="s16"
      width="full"
      onClick={() => field.onClick()}
    >
      <Text.Normal weight="medium" color={field.selected ? 'viridian1200' : 'grey800'}>
        {field.title}
      </Text.Normal>
    </Box>
  )
}

const FilterContent = ({ header, onClickHeader, reset, icon, getFields }: Props) => {
  const { search, onSearch } = useSearch()
  const computedFields: Field[] = useFields(getFields(), search, ASC)
  const fields: Field[] = reset ? getFields() : computedFields

  return (
    <Box padding="s32" gap="s16">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        <Box flexDirection="row" alignItems="center">
          {icon && (
            <Touchable onClick={onClickHeader}>
              <Box paddingRight="s16">
                <Icon.Small icon={icon} color="grey800" />
              </Box>
            </Touchable>
          )}
          <Text size="300" weight="medium" color="grey1000">
            {header}
          </Text>
        </Box>
        {reset && (
          <Button type="secondary" onClick={onClickHeader}>
            {reset}
          </Button>
        )}
      </Box>
      <Box
        borderRadius={{ all: 'abs008' }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        {!reset && (
          <Box width="full" flexDirection="row" background="grey200" padding="s8">
            <Box flexGrow>
              <SearchField
                variant="big"
                search={search}
                onSearch={onSearch}
                placeholder={i18n.t('common.search')}
                autofocus
              />
            </Box>
          </Box>
        )}
        <ScrollableList size="small">
          {!reset && !computedFields.length && <BlankList />}
          {fields.map((field) => (
            <FieldOptions field={field} key={field.title} />
          ))}
        </ScrollableList>
      </Box>
    </Box>
  )
}

export default FilterContent
